import type { Carer } from '@lmc/ts-common'
import { carerHomeIds } from './siteAccess'

export type StatusFilter = 'active' | 'inactive'

// Client-side filters applied on top of the tab + search + sort each team-list
// screen already does (CM-3581). Status maps to `carer.active`; site maps to the
// carer's resolved home ids. Empty arrays mean "no constraint for this facet".
export type TeamFilters = {
  statuses: StatusFilter[]
  siteIds: string[]
}

export const EMPTY_FILTERS: TeamFilters = { statuses: [], siteIds: [] }

// Default filters shown on initial load — 'Active' is selected by default (CM-3788)
export const DEFAULT_FILTERS: TeamFilters = {
  statuses: ['active'],
  siteIds: []
}

// Number of filter facets (Status, Site) whose selection differs from the
// default view (DEFAULT_FILTERS — Active only). Drives the group dashboard's
// Filters badge, so the default Active view shows no badge and the badge counts
// each *changed facet* — not each selected option. Examples:
//   Active only            → 0 (matches the default)
//   Inactive only          → 1 (Status changed)
//   Active + Inactive      → 1 (Status changed)
//   no Status selected      → 1 (Status changed)
//   Active + any site(s)   → 1 (Site changed)
//   Inactive + any site(s) → 2 (both facets changed)
const sameSelection = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value) => b.includes(value))

export const countChangedFilters = (filters: TeamFilters): number => {
  const statusChanged = !sameSelection(
    filters.statuses,
    DEFAULT_FILTERS.statuses
  )
  const siteChanged = !sameSelection(filters.siteIds, DEFAULT_FILTERS.siteIds)
  return (statusChanged ? 1 : 0) + (siteChanged ? 1 : 0)
}

const matchesStatus = (carer: Carer, statuses: StatusFilter[]): boolean => {
  if (!statuses.length) {
    return true
  }
  return statuses.some((status) =>
    status === 'active' ? !!carer.active : !carer.active
  )
}

const matchesSite = (carer: Carer, siteIds: string[]): boolean => {
  if (!siteIds.length) {
    return true
  }
  const ids = new Set(carerHomeIds(carer))
  return siteIds.some((id) => ids.has(String(id)))
}

export const applyTeamFilters = (
  carers: Carer[],
  filters: TeamFilters
): Carer[] =>
  carers.filter(
    (carer) =>
      matchesStatus(carer, filters.statuses) &&
      matchesSite(carer, filters.siteIds)
  )
